// src/components/common/DragDropFileUpload.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IoCloudUploadOutline,
  IoEye,
  IoAlertCircleOutline,
  IoClose
} from 'react-icons/io5';

const DragDropFileUpload = ({
  title,
  name,
  acceptedFormats = ".pdf",
  value,
  onChange,
  error,
  required = false,
  existingFile = null, // should be a URL/path string if already uploaded (edit flow)
  maxFileSize = 10
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [sizeError, setSizeError] = useState(null);

  // Preview dialog
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Holds blob URL for newly selected File
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);

  const inputRef = useRef(null);

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Check file size
  const validateFileSize = (file) => {
    const fileSize = file.size / 1024 / 1024; // MB
    if (fileSize > maxFileSize) {
      setSizeError(`File size exceeds ${maxFileSize}MB limit`);
      return false;
    }
    setSizeError(null);
    return true;
  };

  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      if (validateFileSize(file)) {
        const event = {
          target: {
            name: name,
            files: e.dataTransfer.files
          }
        };
        onChange(event);
      }
    }
  };

  // Handle manual file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (validateFileSize(file)) {
        onChange(e);
      } else {
        e.target.value = '';
      }
    }
  };

  // Handle button click to open file explorer
  const handleButtonClick = () => {
    inputRef.current && inputRef.current.click();
  };

  // Get file name to display
  const getDisplayFileName = () => {
    if (value) {
      return value.name;
    } else if (existingFile) {
      // handles "folder\file.pdf" or "folder/file.pdf"
      const parts = existingFile.split(/[/\\]/);
      return parts[parts.length - 1];
    }
    return null;
  };

  const fileName = getDisplayFileName();

  /**
   * Build a preview URL:
   * - Create flow: value is a File => URL.createObjectURL(value)
   * - Edit flow: existingFile is already a URL/path => use it directly
   */
  const previewSrc = useMemo(() => {
    if (localPreviewUrl) return localPreviewUrl;
    if (existingFile) return existingFile;
    return null;
  }, [localPreviewUrl, existingFile]);

  // Create & cleanup blob URL whenever "value" changes (create flow)
  useEffect(() => {
    // Revoke old url
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
    }

    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setLocalPreviewUrl(url);
    }

    // cleanup on unmount
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const openPreview = () => setIsPreviewOpen(true);
  const closePreview = () => setIsPreviewOpen(false);

  const onOverlayClick = () => closePreview();
  const stopBubble = (e) => e.stopPropagation();

  return (
    <>
      <div
        className={[
          'drag-drop-upload',
          'w-full',
          'rounded-2xl',
          'border',
          'bg-white',
          'p-4',
          'shadow-sm',
          'transition',
          'dark:bg-slate-900',
          dragActive
            ? 'border-blue-300 ring-2 ring-blue-500/20 dark:border-blue-900/50 dark:ring-blue-500/20'
            : error || sizeError
              ? 'border-red-300 ring-1 ring-red-500/10 dark:border-red-900/50 dark:ring-red-500/20'
              : 'border-slate-200 dark:border-slate-800'
        ].join(' ')}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="upload-content flex flex-col items-center text-center">
          <div className="upload-icon inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
            <IoCloudUploadOutline className="text-2xl" />
          </div>

          <p className="upload-title mt-3 text-sm font-semibold text-slate-900 dark:text-white">
            {title}
            {required && <span className="ml-1 text-red-500">*</span>}
          </p>

          <p className="upload-instruction mt-1 text-xs text-slate-600 dark:text-slate-300">
            {fileName ? "Change file" : "Drag & Drop to Upload File"}
          </p>

          <p className="upload-separator my-3 text-[11px] font-semibold tracking-wide text-slate-400 dark:text-slate-500">
            OR
          </p>

          <input
            type="file"
            name={name}
            id={name}
            accept={acceptedFormats}
            onChange={handleFileChange}
            className="file-input hidden"
            ref={inputRef}
            aria-required={required}
            tabIndex={-1}
            aria-hidden="true"
          />

          <button
            type="button"
            className="browse-file-btn inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 dark:focus:ring-blue-500/40"
            onClick={handleButtonClick}
            tabIndex={0}
            aria-label={`Browse for ${title}`}
          >
            Browse File
          </button>

          {fileName && (
            <button
              type="button"
              className="selected-file mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
              onClick={openPreview}
              aria-label={`View ${title}`}
            >
              <IoEye />
              <span title={fileName}>View File</span>
            </button>
          )}

          {(error || sizeError) && (
            <div
              className="upload-error mt-3 w-full rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-left text-xs font-medium text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200"
              role="alert"
            >
              <div className="flex items-start gap-2">
                <IoAlertCircleOutline className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                  {error ? <div>{error}</div> : null}
                  {sizeError ? <div>{sizeError}</div> : null}
                </div>
              </div>
            </div>
          )}

          <div className="upload-size-limit mt-3 text-[11px] text-slate-500 dark:text-slate-400">
            Maximum file size: {maxFileSize}MB
          </div>
        </div>
      </div>

      {/* ------------ Preview Dialog ------------ */}
      {isPreviewOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6"
          onClick={onOverlayClick}
          aria-hidden="false"
        >
          <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm" />

          <div
            className="relative w-full max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900"
            role="dialog"
            aria-modal="true"
            aria-label={`${title} Preview`}
            onClick={stopBubble}
          >
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                  {title} Preview
                </p>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {fileName || 'Selected file'}
                </p>
              </div>

              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
                aria-label="Close preview"
                onClick={closePreview}
              >
                <IoClose className="text-xl" />
              </button>
            </div>

            <div className="ddf-modal-body h-[70vh] w-full bg-slate-50 dark:bg-slate-950/40">
              {(previewSrc || fileName) ? (
                <iframe
                  src={previewSrc || fileName}
                  title="File Preview"
                  className="ddf-iframe h-full w-full"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-sm text-slate-600 dark:text-slate-300">
                  No preview available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DragDropFileUpload;
